// Grid list page: heading "Grids N active · M trades", hide-stopped toggle, + New Grid button.
import { useState } from "react";
import { useGrids } from "@/hooks/use-grids";
import { useGridPnl } from "@/hooks/use-grid-pnl";
import { useOverview } from "@/hooks/use-overview";
import GridCard from "@/components/grid/grid-card";
import GridStoppedCard from "@/components/grid/grid-stopped-card";
import CreateGridModal from "@/components/grid/create-grid-modal";
import type { GridConfig } from "@/types/api";
import type { UnpairedTrade } from "@/hooks/use-overview";

// Per-grid pnl loader — isolates each grid's query so they cache independently.
function GridCardWrapper({
  grid,
  unpaired,
  volatility,
}: {
  grid: GridConfig;
  unpaired: UnpairedTrade[];
  volatility: Record<string, { mode?: string; trend?: string }>;
}) {
  const { data: pnl } = useGridPnl(grid.id);
  if (grid.status === "stopped") {
    return <GridStoppedCard grid={grid} pnl={pnl} />;
  }
  return (
    <GridCard
      grid={grid}
      unpaired={unpaired.filter((t) => t.grid_id === grid.id)}
      pnl={pnl}
      volatility={volatility}
    />
  );
}

export default function GridListPage() {
  const { data: grids, isLoading, error } = useGrids();
  const { data: overview } = useOverview();
  const [hideStopped, setHideStopped] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const unpaired = overview?.unpaired ?? [];
  const volatility = (overview?.volatility ?? {}) as Record<string, { mode?: string; trend?: string }>;
  const pnl = overview?.pnl;

  const activeCount = grids?.filter((g) => g.status === "active").length ?? 0;
  const tradeCount = pnl?.trade_count ?? 0;

  const visible = hideStopped
    ? (grids ?? []).filter((g) => g.status !== "stopped")
    : grids ?? [];

  return (
    <div>
      {/* Heading row — flex-wrap so buttons stay side-by-side on narrow
          screens and drop below the heading instead of each button wrapping
          to its own row. */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-sm font-semibold text-text">
          Grids{" "}
          <span className="text-muted font-normal text-xs">
            {activeCount} active · {tradeCount} trades
          </span>
        </h2>
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <button
            className={`inline-flex items-center h-7 px-3 text-xs font-medium rounded border transition-colors ${
              hideStopped
                ? "border-accent/60 text-accent bg-accent/10"
                : "border-border text-muted hover:text-text"
            }`}
            onClick={() => setHideStopped((v) => !v)}
          >
            Hide stopped
          </button>
          <button
            className="inline-flex items-center h-7 px-3 text-xs font-medium rounded border border-accent/50 text-accent bg-accent/10 hover:bg-accent/20 transition-colors"
            onClick={() => setShowCreate(true)}
          >
            + New Grid
          </button>
        </div>
      </div>

      {/* Grid cards */}
      {isLoading && (
        <div className="text-muted text-sm text-center py-8">Loading grids…</div>
      )}
      {error && (
        <div className="text-danger text-sm text-center py-4">
          Failed to load grids: {(error as Error).message}
        </div>
      )}
      {!isLoading && visible.length === 0 && (
        <div className="text-muted text-sm text-center py-8">No grids yet</div>
      )}

      <div className="space-y-2">
        {visible.map((g) => (
          <GridCardWrapper
            key={g.id}
            grid={g}
            unpaired={unpaired}
            volatility={volatility}
          />
        ))}
      </div>

      {showCreate && <CreateGridModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
